import { Close } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/joy';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { debounce } from 'lodash';
import {
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FormField, IInputProps, SlidingPanel } from 'core/components';
import { Loader } from 'core/elements';
import { InputType } from 'core/enums';
import { GetInfiniteQueryResult, IInfiniteQueryOptions } from 'core/types';
import { enterPress, removeNullValuesNestedObject, trimFilterText } from 'core/utils';
import { Color, Level, Variant } from 'theme/enums';

import { SearchListType } from './enums';
import { List } from './List';

const DEBOUNCE_TIME = 500;

interface IProps<T extends { id: number }> {
  isOpen?: boolean;
  onClose?: () => void;
  title: string;
  defaultQuery?: string;
  onSearchQuery?: (query?: string) => void;
  searchPayload?: Record<string, unknown>;
  setQueryToRequestPayload?: (query: string) => Record<string, unknown>;
  useGetSearchList: (
    requestPayload: Record<string, unknown>,
    options: IInfiniteQueryOptions<T>
  ) => GetInfiniteQueryResult<T>;
  getItemKey?: (item: T) => string;
  renderItem: (item: T, onSelectItemClick: (item: T) => void) => ReactNode;
  renderDetails?: (
    isDetailsOpen: boolean,
    onCloseDetails: () => void,
    selectedItem: T | null
  ) => ReactNode;
  level?: number;
  type?: SearchListType;
  searchInputProps?: IInputProps;
  inlineFilters?: ReactNode;
  appliedFilters?: ReactNode;
}

const DEFAULT_PAGE_SIZE = 20;

export const SearchList = <T extends { id: number }>({
  isOpen,
  onClose,
  title,
  defaultQuery = '',
  onSearchQuery,
  searchPayload = {},
  setQueryToRequestPayload = defaultSetQueryToRequestPayload,
  useGetSearchList,
  getItemKey,
  renderItem,
  renderDetails,
  level = 0,
  type = SearchListType.COMPACT,
  searchInputProps,
  inlineFilters,
  appliedFilters,
}: IProps<T>) => {
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    setInputValue(defaultQuery);
    setQuery(trimFilterText(defaultQuery));
  }, [defaultQuery]);

  const { t } = useTranslation();

  const requestPayload = useMemo(
    () =>
      removeNullValuesNestedObject({
        ...searchPayload,
        pageSize: DEFAULT_PAGE_SIZE,
        ...setQueryToRequestPayload(query),
      }),
    [searchPayload, query, setQueryToRequestPayload]
  );

  const { records, count, hasNextPage, fetchNextPage, isLoading, refetch, errorMessage } =
    useGetSearchList(requestPayload, { enabled: isOpen });

  const onSearch = useCallback(
    (query?: string) => {
      const trimmedQuery = trimFilterText(query);

      setQuery(trimmedQuery);
      onSearchQuery?.(trimmedQuery);
    },
    [onSearchQuery]
  );

  const debouncedSetQuery = debounce(onSearch, DEBOUNCE_TIME);

  const disabledSearchButton = useMemo(
    () => type === SearchListType.FULL && !inputValue,
    [inputValue, type]
  );

  const onSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);

      if (type === SearchListType.COMPACT) {
        debouncedSetQuery(e.target.value);

        return;
      }
    },
    [debouncedSetQuery, type]
  );

  const onSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      enterPress(e, () => onSearch(e.target.value));
    },
    [onSearch]
  );

  const onSearchClear = useCallback(() => {
    setQuery('');
    onSearchQuery?.('');
    setInputValue('');
    refetch();
  }, [onSearchQuery, refetch]);

  const handleOnClose = useCallback(() => {
    onSearchClear();

    onClose?.();
  }, [onClose, onSearchClear]);

  const content = useMemo(() => {
    return (
      <>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {inlineFilters}

          <FormField
            key="search"
            name="search"
            type={InputType.TEXT_INPUT}
            inputProps={{
              placeholder: t('labelSearch'),
              onChange: onSearchChange,
              onKeyDown: onSearchKeyDown,
              value: inputValue || '',
              endDecorator: !!inputValue && (
                <Button color={Color.PRIMARY} icon={<Close />} onClick={onSearchClear} />
              ),
              ...searchInputProps,
            }}
          />

          {type === SearchListType.FULL && (
            <Button
              color={Color.PRIMARY}
              variant={disabledSearchButton ? Variant.SOFT : Variant.SOLID}
              startDecorator={<MagnifyingGlass />}
              text={t('buttonLabelSearch')}
              onClick={() => onSearch(inputValue)}
              disabled={disabledSearchButton}
            />
          )}
        </Box>

        <Stack gap={1}>
          {!isLoading && (
            <Typography level={Level.BODY_SM} {...(errorMessage && { color: Color.DANGER })}>
              {errorMessage || (count ? t('labelResults', { count }) : t('labelNoResults'))}
            </Typography>
          )}

          {appliedFilters}

          {isLoading && <Loader />}

          <List
            records={records}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            getItemKey={getItemKey}
            renderItem={renderItem}
            renderDetails={renderDetails}
          />
        </Stack>
      </>
    );
  }, [
    t,
    onSearchChange,
    onSearchKeyDown,
    inputValue,
    onSearchClear,
    searchInputProps,
    type,
    disabledSearchButton,
    inlineFilters,
    appliedFilters,
    isLoading,
    count,
    errorMessage,
    records,
    hasNextPage,
    fetchNextPage,
    getItemKey,
    renderItem,
    renderDetails,
    onSearch,
  ]);

  if (type === SearchListType.FULL) {
    return content;
  }

  return (
    <SlidingPanel isOpen={isOpen} onClosePanel={handleOnClose} title={title} level={level}>
      {content}
    </SlidingPanel>
  );
};

const defaultSetQueryToRequestPayload = (query: string) => ({ query });
